import type {
  TableCacheKeyDto,
  TableDataDto,
} from '@asset-metrix/portal-gateway-client-js';
import type { StateCreator } from 'zustand';

import type { RemoveFunctions } from '../../utils/types';

export type TableRowSelection = {
  widgetId: string;
  /**
   * Kind of data the rows come from, so readers can tell selected documents
   * (`docview`) from plain data rows.
   */
  dataSourceType: TableCacheKeyDto['dataSourceType'];
  rows: TableDataDto['items'];
};

export type TableRowSelectionSlice = {
  /**
   * Rows selected in the table the user last selected in. Only one table holds
   * a selection at a time: a later non-empty selection replaces an earlier one,
   * and an empty selection from that same table clears the slot.
   */
  tableRowSelection: TableRowSelection | null;

  setTableRowSelection(
    widgetId: string,
    dataSourceType: TableCacheKeyDto['dataSourceType'],
    rows: TableDataDto['items']
  ): void;
  /**
   * Drops the selection, but only for the table that holds it, so a table
   * unmounting elsewhere on the page cannot discard someone else's selection.
   */
  clearTableRowSelection(widgetId: string): void;
  resetTableRowSelectionSlice(): void;
};

const initialState: RemoveFunctions<TableRowSelectionSlice> = {
  tableRowSelection: null,
};

export const createTableRowSelectionSlice: StateCreator<
  TableRowSelectionSlice,
  [],
  [],
  TableRowSelectionSlice
> = (set) => ({
  ...initialState,

  setTableRowSelection(widgetId, dataSourceType, rows) {
    set((state) => {
      if (rows.length === 0) {
        // Same ownership rule as clear: an empty event from another table
        // (init, refetch, pagination) must not drop this table's selection.
        return state.tableRowSelection?.widgetId === widgetId
          ? { tableRowSelection: null }
          : {};
      }

      return { tableRowSelection: { widgetId, dataSourceType, rows } };
    });
  },

  clearTableRowSelection(widgetId) {
    set((state) =>
      state.tableRowSelection?.widgetId === widgetId
        ? { tableRowSelection: null }
        : {}
    );
  },

  resetTableRowSelectionSlice() {
    set({ ...initialState });
  },
});
